import {
  VaccinationActApiInterface as ProVaccinationActApiInterface,
  VaccinationAct as ProVaccinationAct,
  CreateVaccinationActBodyVaccinationAct as ProCreateVaccinationAct,
} from "@syadem/kairos-pro-js";
import {
  VaccinationActApiInterface as TeamVaccinationActApiInterface,
  VaccinationAct as TeamVaccinationAct,
  CreateVaccinationActBodyVaccinationAct as TeamCreateVaccinationAct,
} from "@syadem/kairos-team-js";
import { AppStore } from "../store";
import { Service } from "./service";
import { ActionResult } from "../actionResult";

interface Payload {
  vaccinationAct: ProCreateVaccinationAct | TeamCreateVaccinationAct;
  organizationId: string | null;
  teamId: string | null;
  healthRecordId: string;
}

function normalizeVaccinationAct<T extends ProCreateVaccinationAct | TeamCreateVaccinationAct>(vaccinationAct: T): T {
  const normalizeValue = (value: unknown): unknown => {
    if (value === "" || value === undefined) return null;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) return value.map(normalizeValue);

    if (value !== null && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([field, fieldValue]) => [field, normalizeValue(fieldValue)]),
      );
    }

    return value;
  };

  return normalizeValue(vaccinationAct) as T;
}

export class CreateVaccinationActService implements Service<Payload, ProVaccinationAct | TeamVaccinationAct> {
  constructor(
    private readonly proVaccinationactapi: ProVaccinationActApiInterface,
    private readonly teamVaccinationactapi: TeamVaccinationActApiInterface,
    private readonly store: AppStore,
  ) {}

  async call(
    { vaccinationAct, healthRecordId, organizationId, teamId }: Payload
  ): Promise<ActionResult<ProVaccinationAct | TeamVaccinationAct>> {
    let apiVaccinationAct: ProVaccinationAct | TeamVaccinationAct;

    const normalizedVaccinationAct = normalizeVaccinationAct(vaccinationAct);

    if (organizationId && teamId) {
      apiVaccinationAct = (
        await this.teamVaccinationactapi.createVaccinationAct(
          organizationId,
          teamId,
          healthRecordId,
          { vaccinationAct: normalizedVaccinationAct as TeamCreateVaccinationAct }
        )
      ).vaccinationAct;
    } else {
      apiVaccinationAct = (
        await this.proVaccinationactapi.createVaccinationAct(healthRecordId, {
          vaccinationAct: {
            ...normalizedVaccinationAct,
            vaccinator: normalizedVaccinationAct.vaccinator ?? undefined,
          },
        })
      ).vaccinationAct;
    }

    this.addVaccinationActInStore(healthRecordId, apiVaccinationAct);

    return {
      type: "success",
      value: apiVaccinationAct,
    };
  }

  private addVaccinationActInStore(healthRecordId: string, vaccinationAct: ProVaccinationAct | TeamVaccinationAct) {
    this.store.setState((state) =>
      state.healthRecords && state.healthRecords[healthRecordId]
        ? {
            healthRecords: {
              ...state.healthRecords,
              [healthRecordId]: {
                ...state.healthRecords[healthRecordId],
                vaccinationActs: [vaccinationAct, ...state.healthRecords[healthRecordId].vaccinationActs],
              },
            },
          }
        : state,
    );
  }
}
